import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import InsightsIcon from '@mui/icons-material/Insights';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Message } from '@/types/session';
import type { ConfigSnapshot } from '@/types/logicChain';
import { BRAIN_REGION_COLORS, BRAIN_REGION_LABELS, STAGE_LABELS } from '@/utils/constants';
import StreamingText from './StreamingText';
import { useInsightStore } from '@/stores/useInsightStore';
import FeedbackButtons from '@/components/common/FeedbackButtons';
import { useLogicChain } from '@/hooks/useLogicChain';

interface MessageBubbleProps {
  message: Message;
  turnNumber?: number;
}

type PromptDetail = NonNullable<ConfigSnapshot['prompt_detail']> & {
  prompt_components?: NonNullable<ConfigSnapshot['prompt_detail']>['prompt_components'] & {
    rag_reference_prompt?: string;
  };
  current_summary?: string;
  stage_analysis_source?: string;
  stage_analysis_error?: string;
};

const PromptPre: React.FC<{ children: React.ReactNode; maxHeight?: number }> = ({ children, maxHeight = 360 }) => (
  <Box
    component="pre"
    sx={{
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: 13,
      lineHeight: 1.6,
      bgcolor: '#F7F4EF',
      border: '1px solid #E8E2D9',
      borderRadius: 1,
      p: 2,
      maxHeight,
      overflow: 'auto',
      m: 0,
    }}
  >
    {children}
  </Box>
);

/**
 * 单条消息气泡组件。
 * - user：右对齐，暖棕背景
 * - assistant：左对齐，白色背景带边框
 * - 支持 Markdown 渲染和流式光标
 */
const MessageBubble: React.FC<MessageBubbleProps> = ({ message, turnNumber }) => {
  const isUser = message.role === 'user';
  const isStreaming = message.metadata?.isStreaming ?? false;
  const brainRegion = message.metadata?.brainRegion;
  const stage = message.metadata?.stage;

  const brainRegionColor = brainRegion ? BRAIN_REGION_COLORS[brainRegion] : undefined;
  const brainRegionLabel = brainRegion ? BRAIN_REGION_LABELS[brainRegion] : undefined;
  const stageLabel = stage ? STAGE_LABELS[stage] : undefined;

  const { openPanel, setActiveMessageId } = useInsightStore();
  const { fetchChainByMessage } = useLogicChain();
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptDetail, setPromptDetail] = useState<PromptDetail | null>(null);
  const [promptFallbackText, setPromptFallbackText] = useState('');

  const handleViewLogic = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (message.message_id) {
      setActiveMessageId(message.message_id);
      openPanel();
    }
  };

  const handleViewPrompt = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!message.message_id) return;
    const chain = await fetchChainByMessage(message.message_id);
    const detail = chain?.config_snapshot?.prompt_detail;
    setPromptDetail((detail as PromptDetail | undefined) || null);
    setPromptFallbackText(detail ? '' : '暂未记录提示词详情');
    setPromptOpen(true);
  };

  const promptComponents = promptDetail?.prompt_components;
  const promptMessages = promptDetail?.messages || [];
  const componentSections: Array<[string, string | undefined]> = [
    ['Base System Prompt', promptComponents?.base_system_prompt],
    ['会话阶段分析提示词', promptComponents?.stage_analysis_prompt],
    ['历史会话总结提示词', promptComponents?.history_summary_prompt],
    ['客户信息总结提示词', promptComponents?.customer_profile_prompt],
    ['RAG参考资料提示词', promptComponents?.rag_reference_prompt],
  ].filter((item): item is [string, string] => Boolean(item[1]));

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 1,
          maxWidth: '85%',
        }}
      >
        {/* 头像/图标 */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            flexShrink: 0,
            mt: 0.5,
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'primary.contrastText' : 'text.secondary',
          }}
        >
          {isUser ? '👤' : '🤖'}
        </Box>

        {/* 气泡内容 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {turnNumber && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textAlign: isUser ? 'right' : 'left',
                px: 0.5,
                lineHeight: 1.2,
              }}
            >
              第 {turnNumber} 轮
            </Typography>
          )}
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: isUser ? 'primary.main' : 'background.paper',
              color: isUser ? 'primary.contrastText' : 'text.primary',
              borderRadius: 2,
              border: isUser ? 'none' : '1px solid',
              borderColor: 'divider',
            }}
          >
            {isUser ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {message.content}
              </Typography>
            ) : (
              <Box className="markdown-body" sx={{ fontSize: 14, lineHeight: 1.6 }}>
                <ReactMarkdown>{message.content}</ReactMarkdown>
                {isStreaming && message.content.length > 0 && (
                  <StreamingText text={message.content} isStreaming={isStreaming} />
                )}
              </Box>
            )}
          </Paper>

          {/* 脑区标签 + 查看逻辑按钮（仅 assistant） */}
          {!isUser && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', pl: 0.5 }}>
              {(brainRegionLabel || stageLabel) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: brainRegionColor || 'text.secondary',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.3,
                  }}
                >
                  🧠 {brainRegionLabel}
                  {stageLabel && ` · ${stageLabel}`}
                </Typography>
              )}
              {message.message_id && (
                <>
                <IconButton
                  size="small"
                  onClick={handleViewLogic}
                  sx={{
                    color: '#A99F8E',
                    p: 0.3,
                    opacity: 0.6,
                    '&:hover': { opacity: 1, color: '#7C6F5C' },
                  }}
                  title="查看生成逻辑"
                >
                  <InsightsIcon fontSize="small" sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleViewPrompt}
                  sx={{
                    color: '#A99F8E',
                    p: 0.3,
                    opacity: 0.6,
                    '&:hover': { opacity: 1, color: '#7C6F5C' },
                  }}
                  title="查看提示词详情"
                >
                  <ArticleIcon fontSize="small" sx={{ fontSize: 14 }} />
                </IconButton>
                </>
              )}
            </Box>
          )}

          {/* 反馈按钮：仅 assistant 消息且非流式时显示 */}
          {!isUser && !isStreaming && message.message_id && (
            <FeedbackButtons messageId={message.message_id} />
          )}
        </Box>
      </Box>
      <Dialog open={promptOpen} onClose={() => setPromptOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>提示词详情</DialogTitle>
        <DialogContent>
          {promptDetail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Model: {promptDetail.model || message.model_label || '未知'}
              </Typography>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  最终 System Prompt
                </Typography>
                <PromptPre maxHeight={420}>{promptDetail.system_prompt || '无'}</PromptPre>
              </Box>

              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    提示词组成来源
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                    以下内容为调试拆解，不会再次发送给模型；最终发送内容以上方“最终 System Prompt”为准。
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {componentSections.length ? (
                      componentSections.map(([title, value]) => (
                        <Box key={title}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                            {title}
                          </Typography>
                          <PromptPre maxHeight={260}>{value || '无'}</PromptPre>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        无组成来源记录
                      </Typography>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    阶段分析结果
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <PromptPre maxHeight={320}>
                    {promptDetail.stage_decision ? JSON.stringify(promptDetail.stage_decision, null, 2) : '无'}
                  </PromptPre>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    最近会话总结
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <PromptPre maxHeight={220}>
                    {promptDetail.current_summary ||
                      (promptDetail.recent_summaries?.length ? promptDetail.recent_summaries.join('\n') : '无')}
                  </PromptPre>
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    发送给模型的 Messages
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <PromptPre maxHeight={320}>
                    {promptMessages.length
                      ? promptMessages.map((item, index) => `${index + 1}. ${item.role}\n${item.content}`).join('\n\n')
                      : '无'}
                  </PromptPre>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : (
            <PromptPre>{promptFallbackText || '暂未记录提示词详情'}</PromptPre>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setPromptOpen(false)}>关闭</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MessageBubble;
